import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/api";
import SeoHead from "@/lib/SeoHead";
import InquiryForm from "@/components/InquiryForm";
import { Download, FileText, Layers, ChevronRight, ShieldCheck } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState("specs");

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      if (r.data?.category?.slug) {
        api.get(`/products?category=${r.data.category.slug}&limit=4`).then((rr) => {
          setRelated((rr.data || []).filter((p) => p.id !== r.data.id).slice(0, 3));
        });
      }
    }).catch(() => setProduct({ notFound: true }));
  }, [slug]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C1121F] border-t-transparent rounded-full animate-spin" /></div>;
  if (product.notFound) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
      <h1 className="h-section text-[#1C1C1C]">Product not found</h1>
      <Link to="/products" className="btn-primary mt-6">Back to Catalog</Link>
    </div>
  );

  const productJsonLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name, description: product.short_description || product.description,
    image: product.images, brand: { "@type": "Brand", name: "Durga Auto Industries" },
    sku: product.id,
    category: product.category?.name,
  };

  return (
    <>
      <SeoHead
        title={product.seo_title || `${product.name} | Durga Auto Industries`}
        description={product.seo_description || product.short_description}
        keywords={product.seo_keywords}
        image={product.images?.[0]}
        jsonLd={productJsonLd}
      />

      {/* Breadcrumb */}
      <div className="bg-[#F5F5F5] border-b border-[#E5E7EB]" data-testid="product-breadcrumb">
        <div className="container-x py-4 flex items-center gap-2 text-sm font-body text-[#4B5563]">
          <Link to="/" className="hover:text-[#C1121F]">Home</Link>
          <ChevronRight size={14} />
          <Link to="/products" className="hover:text-[#C1121F]">Products</Link>
          {product.category && (<><ChevronRight size={14} /><Link to={`/products/category/${product.category.slug}`} className="hover:text-[#C1121F]">{product.category.name}</Link></>)}
          <ChevronRight size={14} />
          <span className="text-[#1C1C1C]">{product.name}</span>
        </div>
      </div>

      <section className="section-y-sm bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div data-testid="product-gallery">
            <div className="aspect-square bg-[#F5F5F5] border border-[#E5E7EB] overflow-hidden">
              {product.images?.[activeImg] ? (
                <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#C1121F] font-heading font-bold text-7xl">
                  {product.name?.charAt(0)}
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border-2 transition-colors ${i === activeImg ? "border-[#C1121F]" : "border-[#E5E7EB] hover:border-[#1C1C1C]"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="overline mb-3">{product.category?.name || "Fasteners"}</div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-[#1C1C1C]">{product.name}</h1>
            <p className="mt-4 body-lg text-[#4B5563] font-body">{product.short_description}</p>

            <div className="mt-6 flex items-center gap-2 text-sm font-body text-[#1C1C1C]">
              <ShieldCheck size={18} className="text-[#C1121F]" />
              ISO / DIN / ASTM standards · MTC certificates · Custom orders welcome
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#enquire" className="btn-primary" data-testid="product-cta-enquire">
                Enquire about this product
              </a>
              {product.brochure_url ? (
                <a href={product.brochure_url} target="_blank" rel="noreferrer" className="btn-outline-dark" data-testid="product-cta-brochure">
                  <Download size={16} /> Download Brochure
                </a>
              ) : (
                <Link to="/contact" className="btn-outline-dark" data-testid="product-cta-brochure">
                  <FileText size={16} /> Request Datasheet
                </Link>
              )}
            </div>

            {/* Quick specs */}
            {product.specifications?.length > 0 && (
              <div className="mt-10 border-t border-[#E5E7EB] pt-8">
                <div className="overline mb-4">Quick Specifications</div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {product.specifications.slice(0, 6).map((s, i) => (
                    <div key={i} className="flex justify-between gap-4 text-sm font-body border-b border-[#F5F5F5] pb-2">
                      <dt className="text-[#4B5563]">{s.key}</dt>
                      <dd className="text-[#1C1C1C] font-medium text-right">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tabs: description / specs / applications */}
      <section className="section-y-sm bg-[#F5F5F5]" data-testid="product-tabs">
        <div className="container-x">
          <div className="flex flex-wrap gap-1 border-b border-[#E5E7EB]">
            {[
              ["specs", "Full Specifications", Layers],
              ["desc", "Description", FileText],
              ["apps", "Applications", ShieldCheck],
            ].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-6 py-4 font-heading font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${tab === k ? "border-[#C1121F] text-[#C1121F]" : "border-transparent text-[#4B5563] hover:text-[#1C1C1C]"}`} data-testid={`tab-${k}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <div className="bg-white border border-[#E5E7EB] border-t-0 p-8 md:p-12">
            {tab === "specs" && (
              product.specifications?.length ? (
                <table className="w-full text-sm font-body">
                  <tbody>
                    {product.specifications.map((s, i) => (
                      <tr key={i} className="border-b border-[#F5F5F5]">
                        <td className="py-3 font-heading font-medium text-[#4B5563] w-1/3">{s.key}</td>
                        <td className="py-3 text-[#1C1C1C]">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="font-body text-[#4B5563]">Detailed specifications available on request.</p>
            )}
            {tab === "desc" && (
              <div className="prose max-w-none font-body text-[#1C1C1C] whitespace-pre-line leading-relaxed">
                {product.description || product.short_description}
                {product.features?.length > 0 && (
                  <>
                    <h3 className="font-heading font-semibold text-xl mt-8 mb-4">Key Features</h3>
                    <ul className="space-y-2">{product.features.map((f, i) => <li key={i}>• {f}</li>)}</ul>
                  </>
                )}
              </div>
            )}
            {tab === "apps" && (
              product.applications?.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {product.applications.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 border border-[#E5E7EB] hover:border-[#C1121F] transition-colors">
                      <span className="w-6 h-6 bg-[#C1121F] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <span className="font-body text-[#1C1C1C]">{a}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="font-body text-[#4B5563]">Used across general engineering applications.</p>
            )}
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <section id="enquire" className="section-y bg-white" data-testid="product-inquiry">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12">
          <div>
            <div className="overline mb-4">Product Enquiry</div>
            <h2 className="h-section text-[#1C1C1C]">Request pricing for {product.name}</h2>
            <p className="mt-4 body-lg text-[#4B5563] font-body">
              Specify quantity, finish, and delivery location — we'll respond with a complete quotation including
              MTC and shipping options.
            </p>
          </div>
          <InquiryForm variant="light" type="product" productId={product.id} productName={product.name} title="Send your requirement" />
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-y-sm bg-[#F5F5F5]" data-testid="related-products">
          <div className="container-x">
            <h2 className="h-card text-[#1C1C1C] mb-8">Related products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/products/${p.slug}`} className="card-light p-6 group">
                  <div className="font-heading font-semibold text-[#1C1C1C] group-hover:text-[#C1121F]">{p.name}</div>
                  <div className="font-body text-sm text-[#4B5563] mt-2 line-clamp-2">{p.short_description}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
